import React, { Suspense, useState } from 'react';
import {
  HTTPErrorAlertContainer,
  reportDtCustomError,
  useHTTPError,
} from 'br2s-ui-common-utils-02';
import {
  Button,
  ContentLink,
  InlineAlert,
  SpinningLoader,
  Toast,
  Typography,
} from 'br2s-ui-componentlibrary_r2_02';
import {
  CAROUSEL_LINKS,
  CAROUSEL_TITLES,
  POPULAR_ESERVICES_HEADERS,
  POPULAR_ESERVICES_LINKS,
} from '../../utilities/constants';
import './Home.scss';
import Officepeople from '@/assets/homepage-office-people.png';
import { PAGE_PATH } from '@/utilities/constants/content';
import { useStore } from '@/redux/store';
import { getTransactions } from '@/components/MegaMenu/megaMenuUtils';
import { Transaction } from '@/components/MegaMenu/megaMenuConstants/transactions';
import { SystemIcon } from 'br2s-ui-componentlibrary_r2_02/dist/foundation/assets/system-icon';
import { useNavigate } from 'react-router-dom';

const PageLoaderModule = React.lazy(() => import('content/PageLoaderModule'));
const IpsFederatedSearchModule = React.lazy(
  () => import('ips/IpsFederatedSearchModule'),
);

export interface TabsProps {
  handleTabClick: (index: number) => void;
}

// istanbul ignore next
const openLink = (url: string) => {
  window.open(url, '_blank');
};

// istanbul ignore next
const HomePageLinkCard = ({
  title,
  links,
}: {
  title: string;
  links: Transaction[];
}) => {
  const navigate = useNavigate();

  const navigateUrl = (href: string, newTab = false) => {
    if (newTab) {
      openLink(href);
      return;
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate(href);
  };

  return (
    <div>
      <div className="column-link-header">
        <Typography label={title} type="subheadline" weight="bold" />
      </div>
      {links.map((regLink) => {
        return (
          <ContentLink
            key={regLink.id}
            id={regLink.id}
            label={regLink.label}
            iconClass={regLink.icon as SystemIcon}
            tag="button"
            onClick={() => navigateUrl(regLink.href, regLink.newTab)}
          />
        );
      })}
    </div>
  );
};

export const HomePageCarousell = () => {
  return (
    <div className="row">
      <div className="col-12 col-lg-4">
        <div className="card-title-wrapper">
          <h3 className="headline-4 headline-4--bold">
            {CAROUSEL_TITLES.INFO_GUIDE}
          </h3>
          <Typography label="Access our guides on registering different business entities and complying with regulatory requirements." />
          <Button
            id="home-page-btn"
            label="View all"
            type="quiet"
            onClick={() => openLink(CAROUSEL_LINKS.INFORMATION_GUIDES)}
          />
        </div>
      </div>
      <div className="col-12 col-lg-8 carousel-container">
        <Suspense fallback={<></>}>
          <PageLoaderModule pagePath={PAGE_PATH.HOME.INFO_GUIDES_CAROUSEL} />
        </Suspense>
      </div>
    </div>
  );
};

export const BusinessInitiative = () => {
  return (
    <div className="business-initiative-container">
      <div className="business-initiative-header">
        <Typography
          type="headline-3"
          weight="bold"
          label=" Enabling your business"
        />
        <Typography
          type="subheadline"
          label="Discover support and resources to grow your business."
        />
      </div>
      <Suspense fallback={<></>}>
        <PageLoaderModule
          pagePath={PAGE_PATH.HOME.ENABLING_BUSINESS_CAROUSEL}
        />
      </Suspense>
    </div>
  );
};

const Home = () => {
  const { toggleLoading } = useStore();
  const { handleHTTPError } = useHTTPError();
  const [toast, setToast] = useState({
    isDisplayed: false,
  });
  const [fedSearchError, setFedSearchError] = useState({
    isDisplayed: false,
    headline: '',
  });
  const [searchToast, setSeachToast] = useState({
    isDisplayed: false,
    description: '',
  });
  // istanbul ignore next
  const hideToast = () => setToast({ isDisplayed: false });
  // istanbul ignore next
  const hidesearchToast = () =>
    setSeachToast({ isDisplayed: false, description: '' });

  // istanbul ignore next
  const handleFederatedSearchError = (error: any) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (error.response?.status === 429) {
      setFedSearchError({
        isDisplayed: true,
        headline: 'We are experiencing high traffic. Please try again later.',
      });
    } else if (error.response?.status === 400) {
      setSeachToast({
        isDisplayed: true,
        description: error.response?.data?.error?.errorDesc,
      });
    } else {
      handleHTTPError(error, { errorType: 'inline' });
      console.error(error);
      reportDtCustomError('GenericHandledError', JSON.stringify(error));
    }
  };

  return (
    <div className="homepage">
      <div className="header-container">
        <HTTPErrorAlertContainer />
        {fedSearchError.isDisplayed && (
          <div className="container">
            <InlineAlert
              id="fedsearch-err"
              type="error"
              subHeadline={fedSearchError.headline}
            >
              <div style={{ paddingTop: '12px' }}>
                <Button
                  id="fedsearch-err-btn"
                  label="Dismiss"
                  type="quiet"
                  onClick={() =>
                    setFedSearchError((prev) => ({
                      ...prev,
                      isDisplayed: false,
                    }))
                  }
                />
              </div>
            </InlineAlert>
          </div>
        )}
        <div className="container">
          <div className="page-header-container">
            <div>
              <Typography
                type="headline-1"
                weight="bold"
                label="Welcome to Bizfile"
              />
              <Typography
                type="headline-4"
                label="Your one-stop digital service portal for business registration, filing and information"
              />
            </div>
            <div className="overview-icon">
              <img src={Officepeople} alt="overview-buy-information" />
            </div>
          </div>

          <div className="overview-glass-container">
            <div className="content federated-search-container">
              <Suspense fallback={<SpinningLoader />}>
                <IpsFederatedSearchModule
                  handleSetToastMessage={(toast) =>
                    setToast({ ...toast, isDisplayed: true })
                  }
                  showGlobalSpinningLoader={(boolean) => toggleLoading(boolean)}
                  handleOnSearchError={handleFederatedSearchError}
                />
              </Suspense>
            </div>
            <div>
              <div className="card-section-container">
                <div className="link-content-wrapper">
                  <Typography label="Popular" type="headline-4" weight="bold" />
                  <div className="column-link-wrapper">
                    {Object.keys(POPULAR_ESERVICES_LINKS).map((key, i) => (
                      <HomePageLinkCard
                        key={key}
                        title={POPULAR_ESERVICES_HEADERS[i]}
                        links={getTransactions(
                          POPULAR_ESERVICES_LINKS[
                            key as keyof typeof POPULAR_ESERVICES_LINKS
                          ] as string[],
                          key,
                        )}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className="card-section-container">
                <HomePageCarousell />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="footer-container">
        <div className="container">
          <BusinessInitiative />
        </div>
      </div>

      <Toast
        id="federated-search-toast"
        {...toast}
        onDismiss={hideToast}
        onDismissClick={hideToast}
      />
      <Toast
        id="error-search-toast"
        {...searchToast}
        headline=""
        type="error"
        onDismiss={hidesearchToast}
        onDismissClick={hidesearchToast}
      />
    </div>
  );
};

export default Home;
